// src/components/Payment/PaymentPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";

const authenticatedFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    window.location.href = '/#/login';
    throw new Error('No auth token');
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
  });
};

const API = "https://simplix.co.za/api";
//const API = "http://127.0.0.1:8000/api";
const PAYMENT_PAGE_CACHE_TTL = 5 * 60 * 1000;

const paymentPageCacheKey = (email) => `payment_page_${email}`;

function getSessionCache(key, ttl = PAYMENT_PAGE_CACHE_TTL) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || "null");
    if (!cached || Date.now() - cached.ts > ttl) return null;
    return cached.data || null;
  } catch {
    return null;
  }
}

function setSessionCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

const MIN_PAYMENT_AMOUNT = 100;
const FIXED_FEE = 2;
function getStoredPayerName() {
  try {
    const profile = JSON.parse(localStorage.getItem("profile") || "{}");

    return (
      profile.firstName ||
      profile.firstname ||
      profile.first_name ||
      profile.new_firstname ||
      profile.name?.split(" ")?.[0] ||
      localStorage.getItem("user_first_name") ||
      ""
    );
  } catch {
    return localStorage.getItem("user_first_name") || "";
  }
}

export default function PaymentPage() {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.hash.split("?")[1] || "");
  const selectedMeter = params.get("meter");

  const [meters, setMeters] = useState([]);
  const [meterId, setMeterId] = useState(selectedMeter || "");
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState((
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim());
  const [name, setName] = useState(getStoredPayerName);
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [paymentContext, setPaymentContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadPaymentPage() {
      setLoading(true);
      setPageError("");

      try {
        const emailStored = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

        if (!emailStored) {
          navigate("/#/login", { replace: true });
          return;
        }

        const cacheKey = paymentPageCacheKey(emailStored);
        const cached = getSessionCache(cacheKey);

        if (cached) {
          setMeters(cached.meters || []);
          if (cached.name && !name) setName(cached.name);

          if (
            selectedMeter &&
            !(cached.meters || []).some(
              (m) => String(m.id) === String(selectedMeter),
            )
          ) {
            setMeterId("");
            setPageError(
              "The selected meter could not be found on your profile.",
            );
          }

          setLoading(false);
          return;
        }

        const [accountsRes, metersRes] = await Promise.all([
          authenticatedFetch(`${API}/accounts`, {
            signal: controller.signal,
          }),
          authenticatedFetch(`${API}/meters`, {
            signal: controller.signal,
          }),
        ]);

        if (!accountsRes.ok) {
          throw new Error("Failed to load account information");
        }

        if (accountsRes.ok) {
          const accountsJson = await accountsRes.json();
          const accounts = Array.isArray(accountsJson?.accounts)
            ? accountsJson.accounts
            : [];

          const hasPending = accounts.some(
            (a) => String(a?.status || "").toLowerCase() !== "active",
          );

          if (hasPending) {
            sessionStorage.setItem(
              "profile_notice",
              "Please upload your ID document for your pending account before using meters, wallets or payments.",
            );
            navigate("/profile", { replace: true });
            return;
          }
        }

        if (!metersRes.ok) throw new Error("Failed to load meters");

        const j = await metersRes.json();

        const profileName =
          j?.profile?.firstName ||
          j?.profile?.firstname ||
          j?.profile?.name?.split(" ")?.[0] ||
          j?.user?.firstName ||
          j?.user?.name?.split(" ")?.[0] ||
          "";

        const mapped = (j?.meters || []).map((m) => ({
          id: m.id,
          meter_number: m.number || m.name || "",
          alias: m.name || "",
          utility_type: m.type || "Electricity",
          payfast_fee_percent:
            m.payfastFeePercent ?? m.payfast_fee_percent ?? null,
          balance:
            Number(
              m.balance ??
                m.running_balance ??
                m.account_balance ??
                m.outstanding_balance ??
                0,
            ) || 0,
        }));

        if (cancelled) return;

        setMeters(mapped);

        if (!name && profileName) setName(profileName);

        setSessionCache(cacheKey, {
          meters: mapped,
          name: profileName,
        });

        if (
          selectedMeter &&
          !mapped.some((m) => String(m.id) === String(selectedMeter))
        ) {
          setMeterId("");
          setPageError(
            "The selected meter could not be found on your profile.",
          );
        }
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          setPageError(
            "We could not load your payment information. Please refresh the page and try again.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPaymentPage();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chosen = useMemo(
    () => meters.find((m) => String(m.id) === String(meterId)),
    [meters, meterId],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadPaymentContext() {
      const emailStored = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

      if (!meterId || !chosen || !emailStored) {
        setPaymentContext(null);
        return;
      }
      try {
        setPaymentContext(null);
        setPageError("");
        setContextLoading(true);

        const r = await authenticatedFetch(
          `${API}/payment/meter-context/?meter_id=${encodeURIComponent(
            meterId,
          )}&email=${encodeURIComponent(emailStored)}`,
          {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );

        if (!r.ok) {
          throw new Error("Failed to load payment context");
        }

        const j = await r.json();

        if (!j?.ok) {
          throw new Error(j?.error || "Payment information is unavailable");
        }

        if (!cancelled) {
          setPaymentContext(j);
        }
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          setPaymentContext(null);
          setPageError(
            "We could not verify the meter status or account balance. Please refresh the page and try again.",
          );
        }
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    }

    loadPaymentContext();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [meterId, chosen]);
  const roundMoney = (value) =>
    Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  const amountNumber = Number(amount || 0);
  const payfastFeeValue =
    paymentContext?.payfast_fee_percent ?? chosen?.payfast_fee_percent ?? null;

  const payfastFeeConfigured =
    payfastFeeValue !== null &&
    payfastFeeValue !== undefined &&
    payfastFeeValue !== "" &&
    Number.isFinite(Number(payfastFeeValue)) &&
    Number(payfastFeeValue) >= 0 &&
    Number(payfastFeeValue) < 1;

  const payfastFeePercent = payfastFeeConfigured ? Number(payfastFeeValue) : 0;

  const payfastFeeDisplayPercent = payfastFeeConfigured
    ? Number((payfastFeePercent * 100).toFixed(4))
    : null;

  const payfastFee =
    amountNumber > 0 && payfastFeeConfigured
      ? roundMoney(amountNumber * payfastFeePercent + FIXED_FEE)
      : 0;

  const netCredit =
    amountNumber > 0 ? roundMoney(amountNumber - payfastFee) : 0;
  const meterIsOff = paymentContext?.is_off === true;
  const reconnectionFee = Number(paymentContext?.reconnection_fee || 0);

  const bcBalance = Number(paymentContext?.bc_balance || 0);

  // In BC:
  // Negative = credit / good
  // Zero or positive = debit / no credit
  const meterStatusChecked = Boolean(paymentContext) && !contextLoading;

  const currentOutstandingBalance = bcBalance > 0 ? bcBalance : 0;

  const isDebit = bcBalance > 0;

  // Meter ON + debit: exact amount to pay so the account clears to credit
  // after PayFast deducts (pct% + R2). i.e. "balance + fee", solved exactly.
  const currentRequired =
    isDebit && payfastFeeConfigured
      ? Math.ceil(
          ((currentOutstandingBalance + FIXED_FEE) / (1 - payfastFeePercent)) *
            100,
        ) / 100
      : currentOutstandingBalance;

  const feePortion = isDebit
    ? roundMoney(currentRequired - currentOutstandingBalance)
    : 0;

  // Meter ON + debit + amount above the R100 floor but below the credit threshold.
  const belowCreditThreshold =
    !meterIsOff &&
    isDebit &&
    amount !== "" &&
    Number.isFinite(amountNumber) &&
    amountNumber >= MIN_PAYMENT_AMOUNT &&
    amountNumber < currentRequired;

  // Leave at least R1.00 credit after the balance,
  // reconnection fee and payment fee are deducted.
  const amountNeededAfterFeesAndReconnect = meterIsOff
    ? roundMoney(currentOutstandingBalance + reconnectionFee + 1)
    : 0;

  const minimumGrossNeeded =
    meterIsOff && amountNeededAfterFeesAndReconnect > 0
      ? Math.ceil(
          Math.max(
            MIN_PAYMENT_AMOUNT,
            (amountNeededAfterFeesAndReconnect + FIXED_FEE) /
              (1 - payfastFeePercent),
          ) * 100,
        ) / 100
      : MIN_PAYMENT_AMOUNT;
  const requiredMinimumPayment = meterIsOff
    ? minimumGrossNeeded
    : MIN_PAYMENT_AMOUNT;

  const amountTooLow = amount !== "" && amountNumber < requiredMinimumPayment;

  const isEnoughForBalance =
    !meterIsOff || netCredit + 0.001 >= amountNeededAfterFeesAndReconnect;

  const paymentBlocked =
    !meterId ||
    !payfastFeeConfigured ||
    !chosen ||
    !amount ||
    Number(amount) < requiredMinimumPayment ||
    preparing ||
    loading ||
    contextLoading ||
    !meterStatusChecked ||
    !isEnoughForBalance;

  function fmtAmount(a) {
    const n = Number(a || 0);
    if (!isFinite(n)) return "0.00";
    return n.toFixed(2);
  }

  async function startPayment(e) {
    e.preventDefault();
    setError("");

    const buyerEmail = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();
    const amt = Number(amount);

    if (!meterId || !chosen) {
      setError(
        "No meter selected. Please start the payment from the meter page.",
      );
      return;
    }

    if (!buyerEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(buyerEmail)) {
      setError("Please provide a valid email address.");
      return;
    }

    if (!meterStatusChecked) {
      setError("Please wait while we check the meter status.");
      return;
    }

    if (!amount || !Number.isFinite(amt) || amt < requiredMinimumPayment) {
      setError(`Please enter at least R ${fmtAmount(requiredMinimumPayment)}.`);
      return;
    }

    if (!isEnoughForBalance) {
      setError(
        `This payment is not enough to reconnect the meter. After PayFast fees and the reconnection fee, your account must return to credit. Please pay at least R ${fmtAmount(
          minimumGrossNeeded,
        )}.`,
      );
      return;
    }

    try {
      setPreparing(true);

      const controller = new AbortController();

      const r = await authenticatedFetch(`${API}/payment/payfast/prepare/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          meterId,
          amount: amt,
          email: buyerEmail,
          name: (name || "").trim(),
        }),
      });

      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || "Could not prepare payment.");
      }

      const j = await r.json();

      if (!j?.action || !j?.fields) {
        throw new Error("Invalid response from payment gateway preparer.");
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = j.action;

      Object.entries(j.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v ?? "";
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setError(err?.message || "Payment failed to start.");
    } finally {
      setPreparing(false);
    }
  }

    const [showCreditConfirm, setShowCreditConfirm] = useState(false);

    function validateForPayment() {
    const buyerEmail = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();
    const amt = Number(amount);

    if (!meterId || !chosen) {
      setError("No meter selected. Please start the payment from the meter page.");
      return false;
    }
    if (!buyerEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(buyerEmail)) {
      setError("Please provide a valid email address.");
      return false;
    }
    if (!meterStatusChecked) {
      setError("Please wait while we check the meter status.");
      return false;
    }
    if (!amount || !Number.isFinite(amt) || amt < requiredMinimumPayment) {
      setError(`Please enter at least R ${fmtAmount(requiredMinimumPayment)}.`);
      return false;
    }
    if (!isEnoughForBalance) {
      setError(
        `This payment is not enough to reconnect the meter. After PayFast fees and the reconnection fee, your account must return to credit. Please pay at least R ${fmtAmount(
          minimumGrossNeeded,
        )}.`,
      );
      return false;
    }
    return true;
  }

  async function doStartPayment() {
    const buyerEmail = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();
    const amt = Number(amount);

    try {
      setPreparing(true);
      const controller = new AbortController();

      const r = await authenticatedFetch(`${API}/payment/payfast/prepare/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          meterId,
          amount: amt,
          email: buyerEmail,
          name: (name || "").trim(),
        }),
      });

      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || "Could not prepare payment.");
      }

      const j = await r.json();

      if (!j?.action || !j?.fields) {
        throw new Error("Invalid response from payment gateway preparer.");
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = j.action;

      Object.entries(j.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v ?? "";
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setError(err?.message || "Payment failed to start.");
    } finally {
      setPreparing(false);
    }
  }

  const configurationError =
    paymentContext && !payfastFeeConfigured
      ? "Payments are not configured correctly for this meter. Please contact support."
      : "";

  const fullPageError = pageError || configurationError;

  const fullPageLoading =
    !fullPageError &&
    (loading ||
      contextLoading ||
      preparing ||
      (Boolean(meterId && chosen) && !paymentContext));

  if (fullPageError) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateIcon}>!</div>
          <h2>Payment information unavailable</h2>
          <p>{fullPageError}</p>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => window.location.reload()}
          >
            Refresh and retry
          </button>

          <button
            type="button"
            className={styles.paymentStateBack}
            onClick={() => navigate("/meters")}
          >
            Return to meters
          </button>
        </div>
      </div>
    );
  }

  if (fullPageLoading) {
    return (
      <div className={styles.paymentStateScreen}>
        <div className={styles.paymentStateCard}>
          <div className={styles.paymentStateSpinner} />

          <h2>
            {preparing ? "Opening secure payment" : "Checking payment details"}
          </h2>

          <p>
            {preparing
              ? "Please wait while we connect you to PayFast."
              : "We are checking your meter status, account balance and applicable fees."}
          </p>

          <strong>Please do not close or refresh this page.</strong>
        </div>
      </div>
    );
  }

  const showCurrentRequired = !meterIsOff && isDebit && meterStatusChecked;

  function startPayment(e) {
    e.preventDefault();
    setError("");

    if (!validateForPayment()) return;

    // Meter ON + in debit + amount below the amount needed to reach credit:
    // warn in a pop-up, but let them continue.
    if (belowCreditThreshold) {
      setShowCreditConfirm(true);
      return;
    }

    doStartPayment();
  }



  return (
    <div className={styles.page}>
      <div
        className={`${styles.section} ${styles.meterDetailSection} ${styles.paymentPageSection}`}
      >
        <div className={styles.paymentGrid}>
          <div className={styles.meterDetailPanel}>
            <div className={styles.meterDetailPanelHeader}>
              <div>
                <p className={styles.sectionEyebrow}>Payment</p>
                <h2>Payment details</h2>
              </div>
            </div>

            <div className={styles.authForm}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "18px 20px",
                  borderRadius: 22,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #eef6ff 100%)",
                  border: "2px solid #1d78ff",
                  boxShadow: "0 0 0 4px rgba(29,120,255,0.12)",
                  color: "#0f172a",
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background:
                      "linear-gradient(135deg, #1d78ff 0%, #0b5ed7 100%)",
                    color: "#fff",
                    fontSize: 28,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  ⚡
                </div>

                <div style={{ flex: 1 }}>
                  <strong
                    style={{
                      display: "block",
                      fontSize: 20,
                      lineHeight: 1.2,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {loading
                      ? "Loading selected meter..."
                      : chosen
                        ? `${chosen.meter_number} • ${
                            chosen.alias || chosen.utility_type
                          }`
                        : "No meter selected"}
                  </strong>

                  <span
                    style={{
                      display: "block",
                      marginTop: 6,
                      color: "#1d78ff",
                      fontWeight: 900,
                      fontSize: 13,
                    }}
                  >
                    🔒 Selected meter locked
                  </span>
                </div>

                <span
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "rgba(29,120,255,0.12)",
                    color: "#1d78ff",
                    fontSize: 12,
                    fontWeight: 900,
                    textTransform: "uppercase",
                  }}
                >
                  Locked
                </span>
              </div>

              {!showCurrentRequired && (
                <div className={styles.paymentFeeNotice}>
                  <span>💳</span>
                  <p>
                    Current balance:{" "}
                    <b
                      style={{
                        color:
                          bcBalance > 0
                            ? "#dc2626" // red = outstanding
                            : bcBalance < 0
                              ? "#16a34a" // green = credit
                              : "#0f172a", // default/settled
                      }}
                    >
                      {bcBalance < 0
                        ? `R ${fmtAmount(Math.abs(bcBalance))} in credit`
                        : bcBalance > 0
                          ? `R ${fmtAmount(bcBalance)} outstanding`
                          : "R 0.00 settled"}
                    </b>
                  </p>
                </div>
              )}

              {showCurrentRequired && (
                <div className={styles.paymentFeeNotice}
                  
                >
                  <p>
                    Current required: <b style={{
                    color: "#dc2626",
                  }}> R {fmtAmount(currentRequired)}*</b>
                  </p>
                </div>
              )}

              <label
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Amount (ZAR)
              </label>

              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 24,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: amountTooLow ? "#dc2626" : "#0f172a",
                    fontWeight: 900,
                    fontSize: 22,
                    zIndex: 1,
                  }}
                >
                  R
                </span>

                <input
                  className={styles.authInput}
                  type="text"
                  inputMode="decimal"
                  placeholder={`Minimum R ${fmtAmount(requiredMinimumPayment)}`}
                  value={amount}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/[^\d.]/g, "");
                    setAmount(clean);
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  style={{
                    paddingLeft: 58,
                    border: amountTooLow
                      ? "2px solid #dc2626"
                      : "2px solid transparent",
                    boxShadow: amountTooLow
                      ? "0 0 0 4px rgba(220,38,38,0.12)"
                      : undefined,
                    color: amountTooLow ? "#dc2626" : undefined,
                    fontWeight: 900,
                  }}
                />
              </div>

              {amountTooLow && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "16px 18px",
                    borderRadius: 18,
                    background:
                      "linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%)",
                    border: "1px solid rgba(220,38,38,0.35)",
                    color: "#991b1b",
                  }}
                >
                  <strong style={{ fontSize: 24 }}>!</strong>
                  <div>
                    <strong style={{ display: "block" }}>
                      Minimum payment is R {fmtAmount(requiredMinimumPayment)}
                    </strong>
                    <span>
                      {meterIsOff
                        ? "This includes the balance, reconnection fee, payment fee and R1.00 credit."
                        : `Please enter R ${fmtAmount(requiredMinimumPayment)} or more to continue.`}
                    </span>
                  </div>
                </div>
              )}

              {belowCreditThreshold && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    padding: "16px 18px",
                    borderRadius: 18,
                    background:
                      "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
                    border: "1px solid rgba(133, 120, 120, 0.4)",
                    color: "#f6edec",
                  }}
                >
                  <strong style={{ fontSize: 22 }}>⚠️</strong>
                  <div>
                    <strong style={{ display: "block" }}>
                      Not enough to put your account in credit
                    </strong>
                    <span>
                      Your payment of R {fmtAmount(amount)} will not fully clear
                      your balance. You can still proceed with the payment. To
                      bring your account to credit, pay at least R{" "}
                      {fmtAmount(currentRequired)}.
                    </span>
                  </div>
                </div>
              )}

              {payfastFeeConfigured ? (
                <div className={styles.paymentFeeNotice}>
                  <span>ℹ️</span>
                  <p>
                    Payment processing fee: {payfastFeeDisplayPercent}% + R{" "}
                    {fmtAmount(FIXED_FEE)}. This fee is deducted from the amount
                    credited to your account.
                  </p>
                </div>
              ) : (
                meterStatusChecked && (
                  <div className={styles.paymentFeeNotice}>
                    <span>⚠️</span>
                    <p>
                      Payments are unavailable because the PayFast fee is not
                      configured for this meter&apos;s building.
                    </p>
                  </div>
                )
              )}

              {meterIsOff && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                    padding: "22px 20px",
                    borderRadius: 22,
                    background:
                      "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
                    border: "3px solid #fecaca",
                    boxShadow: "0 0 0 5px rgba(220,38,38,0.18)",
                    color: "#fff",
                  }}
                >
                  <strong style={{ fontSize: 34 }}>🚨</strong>

                  <div>
                    <strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        fontSize: 18,
                      }}
                    >
                      Meter is switched off
                    </strong>

                    <span>
                      To switch the meter back on, your payment must cover:
                    </span>

                    <div
                      style={{
                        marginTop: 12,
                        lineHeight: 1.8,
                        fontWeight: 800,
                      }}
                    >
                      <div>
                        Outstanding balance: R{" "}
                        {fmtAmount(currentOutstandingBalance)}
                      </div>
                      <div>
                        Reconnection fee: R {fmtAmount(reconnectionFee)}
                      </div>
                      <div>
                        Payment fee: {payfastFeeDisplayPercent}% + R{" "}
                        {fmtAmount(FIXED_FEE)}
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 14,
                        padding: "12px 14px",
                        borderRadius: 14,
                        background: "rgba(255,255,255,0.18)",
                        fontWeight: 900,
                      }}
                    >
                      Minimum payment to restore meter: R{" "}
                      {fmtAmount(minimumGrossNeeded)}
                    </div>

                    {!isEnoughForBalance && amount && (
                      <div style={{ marginTop: 10, fontWeight: 900 }}>
                        The entered amount is not enough to reconnect the meter.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.paymentInfoCard}>
                <div className={styles.paymentInfoIcon}>↗</div>

                <div className={styles.paymentInfoMain}>
                  <strong>What you need to know</strong>
                  <span>
                    Minimum payment is <b>R {fmtAmount(MIN_PAYMENT_AMOUNT)}</b>.
                  </span>
                </div>

                <div className={styles.paymentInfoAmount}>
                  <span>You pay</span>
                  <strong
                    className={amountTooLow ? styles.paymentAmountBad : ""}
                  >
                    R {fmtAmount(amount)}
                  </strong>
                </div>

                <div className={styles.paymentInfoAmount}>
                  <span>You get</span>
                  <strong>
                    {payfastFeeConfigured
                      ? `R ${fmtAmount(netCredit)}`
                      : "Unavailable"}
                  </strong>
                  <small>after fees</small>
                </div>
              </div>

              <input
                className={styles.authInput}
                placeholder="Payer name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <input
                className={styles.authInput}
                type="email"
                placeholder="Email for receipt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {error && <div className={styles.authError}>{error}</div>}

              {showCurrentRequired && (
              <p
                className={styles.meterPanelText}
                style={{ textAlign: "center" }}
              >
                 * = Transaction Fee Included
              </p>)}

              <form
                onSubmit={startPayment}
                className={styles.paymentSubmitForm}
              >
                <button
                  className={styles.primaryBtn}
                  type="submit"
                  disabled={paymentBlocked}
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    opacity: paymentBlocked ? 0.55 : 1,
                  }}
                >
                  {preparing ? "Preparing..." : "🔒 Proceed to Payment"}
                </button>
              </form>

                    {showCreditConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.6)",
            display: "grid",
            placeItems: "center",
            padding: 20,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 440,
              background: "#fff",
              borderRadius: 22,
              padding: "26px 24px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              border: "1px solid rgba(220,38,38,0.25)",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <strong style={{ fontSize: 30 }}>⚠️</strong>
              <div>
                <h3 style={{ margin: "0 0 8px", fontSize: 18, color: "#0f172a" }}>
                  Your account will not be in credit
                </h3>
                <p style={{ margin: 0, color: "#334155", lineHeight: 1.6 }}>
                  Your payment of R {fmtAmount(amountNumber)} will not be enough to put your account in credit. You
                  will still owe R{" "}
                  {fmtAmount(Math.max(0, currentOutstandingBalance - netCredit))}{" "}
                  after this payment.
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
              <button
                type="button"
                className={styles.inlineBtn}
                onClick={() => setShowCreditConfirm(false)}
              >
                Adjust amount
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                style={{ flex: 1 }}
                onClick={() => {
                  setShowCreditConfirm(false);
                  doStartPayment();
                }}
              >
                Continue payment
              </button>
            </div>
          </div>
        </div>
      )}


              <p
                className={styles.meterPanelText}
                style={{ textAlign: "center" }}
              >
                Your payment details are secure and encrypted.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
